import { Alert, Form, InputNumber, Switch } from '@arco-design/web-react'
import SettingSectionCard from '../SettingSectionCard'
import type { OptionsAPI } from '../useOptions'

const THRESHOLD_KEYS = [
  'alert_setting.slow_request_threshold_ms',
  'alert_setting.upstream_slow_threshold_ms',
  'alert_setting.ttft_slow_threshold_ms',
  'alert_setting.error_rate_threshold_percent',
  'alert_setting.upstream_error_rate_threshold_percent',
  'alert_setting.no_channel_alert_enabled',
  'alert_setting.billing_error_alert_enabled',
  'alert_setting.channel_auto_ban_alert_enabled',
  'alert_setting.redis_error_alert_enabled',
  'alert_setting.mysql_error_alert_enabled',
  'alert_setting.disk_usage_threshold_percent',
  'alert_setting.cpu_usage_threshold_percent',
  'alert_setting.memory_usage_threshold_percent',
] as const

export function validatePerformanceThresholds(options: OptionsAPI): string | null {
  const slow = options.getNumber('alert_setting.slow_request_threshold_ms', 15000)
  const upstream = options.getNumber('alert_setting.upstream_slow_threshold_ms', 12000)
  const ttft = options.getNumber('alert_setting.ttft_slow_threshold_ms', 5000)
  if (slow < 1000 || slow > 600000) return '慢请求阈值应在 1000–600000 ms'
  if (upstream < 500 || upstream > 600000) return '上游慢请求阈值应在 500–600000 ms'
  if (ttft < 200 || ttft > 120000) return 'TTFT 阈值应在 200–120000 ms'
  return null
}

export default function PerformanceThresholdCard({ options }: { options: OptionsAPI }) {
  const { getBool, getNumber, patch } = options

  return (
    <SettingSectionCard
      title="性能与告警阈值"
      subtitle="慢请求、错误率与系统资源告警触发条件"
      keys={[...THRESHOLD_KEYS]}
      options={options}
      validate={() => validatePerformanceThresholds(options)}
    >
      <Alert type="info" className="mb-4" content="阈值用于钉钉告警与监控摘要，不影响 Relay 保护阈值（见上方系统性能监控）。" />
      <Form layout="vertical">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Form.Item label="慢请求阈值 (ms)">
            <InputNumber style={{ width: '100%' }} min={1000} max={600000} value={getNumber('alert_setting.slow_request_threshold_ms', 15000)} onChange={(v) => patch({ 'alert_setting.slow_request_threshold_ms': v ?? 15000 })} />
          </Form.Item>
          <Form.Item label="上游慢请求 (ms)">
            <InputNumber style={{ width: '100%' }} min={500} max={600000} value={getNumber('alert_setting.upstream_slow_threshold_ms', 12000)} onChange={(v) => patch({ 'alert_setting.upstream_slow_threshold_ms': v ?? 12000 })} />
          </Form.Item>
          <Form.Item label="TTFT 慢阈值 (ms)">
            <InputNumber style={{ width: '100%' }} min={200} max={120000} value={getNumber('alert_setting.ttft_slow_threshold_ms', 5000)} onChange={(v) => patch({ 'alert_setting.ttft_slow_threshold_ms': v ?? 5000 })} />
          </Form.Item>
          <Form.Item label="错误率阈值 (%)">
            <InputNumber style={{ width: '100%' }} min={1} max={100} value={getNumber('alert_setting.error_rate_threshold_percent', 5)} onChange={(v) => patch({ 'alert_setting.error_rate_threshold_percent': v ?? 5 })} />
          </Form.Item>
          <Form.Item label="上游错误率 (%)">
            <InputNumber style={{ width: '100%' }} min={1} max={100} value={getNumber('alert_setting.upstream_error_rate_threshold_percent', 10)} onChange={(v) => patch({ 'alert_setting.upstream_error_rate_threshold_percent': v ?? 10 })} />
          </Form.Item>
          <Form.Item label="无可用渠道告警">
            <Switch checked={getBool('alert_setting.no_channel_alert_enabled')} onChange={(v) => patch({ 'alert_setting.no_channel_alert_enabled': v })} />
          </Form.Item>
          <Form.Item label="计费异常告警">
            <Switch checked={getBool('alert_setting.billing_error_alert_enabled')} onChange={(v) => patch({ 'alert_setting.billing_error_alert_enabled': v })} />
          </Form.Item>
          <Form.Item label="渠道自动禁用告警">
            <Switch checked={getBool('alert_setting.channel_auto_ban_alert_enabled')} onChange={(v) => patch({ 'alert_setting.channel_auto_ban_alert_enabled': v })} />
          </Form.Item>
          <Form.Item label="Redis 异常告警">
            <Switch checked={getBool('alert_setting.redis_error_alert_enabled')} onChange={(v) => patch({ 'alert_setting.redis_error_alert_enabled': v })} />
          </Form.Item>
          <Form.Item label="MySQL 异常告警">
            <Switch checked={getBool('alert_setting.mysql_error_alert_enabled')} onChange={(v) => patch({ 'alert_setting.mysql_error_alert_enabled': v })} />
          </Form.Item>
          <Form.Item label="磁盘使用率告警 (%)">
            <InputNumber style={{ width: '100%' }} min={50} max={100} value={getNumber('alert_setting.disk_usage_threshold_percent', 85)} onChange={(v) => patch({ 'alert_setting.disk_usage_threshold_percent': v ?? 85 })} />
          </Form.Item>
          <Form.Item label="CPU 告警 (%)">
            <InputNumber style={{ width: '100%' }} min={50} max={100} value={getNumber('alert_setting.cpu_usage_threshold_percent', 90)} onChange={(v) => patch({ 'alert_setting.cpu_usage_threshold_percent': v ?? 90 })} />
          </Form.Item>
          <Form.Item label="内存告警 (%)">
            <InputNumber style={{ width: '100%' }} min={50} max={100} value={getNumber('alert_setting.memory_usage_threshold_percent', 90)} onChange={(v) => patch({ 'alert_setting.memory_usage_threshold_percent': v ?? 90 })} />
          </Form.Item>
        </div>
      </Form>
    </SettingSectionCard>
  )
}
